import { useEffect, useState } from 'react';

import { DEFAULT_HUB_CONNECTION } from '@/constants';
import { useHubProvider, useSiteContext } from '@/providers';

/**
 * Custom hook to manage and track the current site's connection state within a Hub provider.
 *
 * This hook retrieves the current site's connection details, monitors its state, and provides status
 * information such as loading, error, and connection ID. It reacts to connection-related events such as
 * reconnection and closure to update the state accordingly.
 *
 * @function useCurrentSiteConnection
 * @returns {Object} An object containing the following properties:
 * - `isLoading` {boolean}: Indicates whether the connection is in a loading state.
 * - `connection` {Object}: The current site connection object, or a default fallback if no connection exists.
 * - `connectionId` {string}: The unique identifier of the current connection, or an empty string if unavailable.
 * - `isError` {boolean}: Flag indicating whether an error has occurred with the connection.
 */
export const useCurrentSiteConnection = () => {
  const { currentSite } = useSiteContext();
  const { connections, isLoading: isProviderLoading, pendingSites } = useHubProvider();
  const siteConnection = currentSite ? connections?.[currentSite.id] : undefined;

  // This site's connection attempt is still in flight. The provider-wide
  // flag covers the window before the site list (and its queries) exist.
  const isSitePending =
    (isProviderLoading ?? false) ||
    (currentSite ? (pendingSites?.includes(currentSite.id) ?? false) : false);

  const [connectionId, setConnectionId] = useState(siteConnection?.connectionId ?? '');
  const [isLoading, setIsLoading] = useState(isSitePending);
  const [isLostConnection, setIsLostConnection] = useState(false);

  useEffect(() => {
    if (!siteConnection) {
      setIsLoading(isSitePending);
      return;
    }

    setIsLostConnection(false);
    if (siteConnection.connectionId) {
      setConnectionId(siteConnection.connectionId);
      setIsLoading(isSitePending);
    }
  }, [siteConnection, isSitePending]);

  // Register lifecycle handlers once per connection. SignalR has no removal
  // API for these callbacks, so the cleanup makes stale registrations inert
  // instead (re-registering on every state change would stack duplicates).
  useEffect(() => {
    if (!siteConnection) {
      return;
    }
    let active = true;

    siteConnection.onreconnecting(() => {
      if (active) setIsLoading(true);
    });

    siteConnection.onreconnected((connectionId?: string) => {
      if (!active) return;
      console.info(`🟢 ${currentSite?.name}: reconnected (id=${connectionId}).`);
      if (connectionId) {
        setConnectionId(connectionId); // force rerender consumers
      }
      setIsLoading(false);
      setIsLostConnection(false);
    });

    siteConnection.onclose(() => {
      if (!active) return;
      setIsLostConnection(true);
      setIsLoading(false);
    });

    return () => {
      active = false;
    };
  }, [siteConnection, currentSite?.name]);

  return {
    isLoading,
    connection: siteConnection ?? DEFAULT_HUB_CONNECTION,
    connectionId,
    isError: isLostConnection,
  };
};
